import React, { useState, useEffect } from 'react';

import classnames from 'classnames';

import { Typography, Tooltip, Checkbox, Col, Row } from 'antd';
import { EditOutlined } from '@ant-design/icons';

import { ColumnContainer, CollapseView, TextInfo } from './ShareComponents';
import FormEditCustomProductProperties from './FormEditCustomProductProperties';

import {
  useGetMemberProductCustomProperties,
  useGetProductCustomProperties,
} from './queries';
import { useCheckPermissions } from 'hooks';

import { useUserInfo } from 'hooks/auth/useUserInfo';
import {
  PERMISSION_CREATE_PRODUCTS,
  PERMISSION_EDIT_PRODUCTS,
} from 'static/Permission';

const keyPanel = 'Private Properties';

const customPropertiesEditPermissions = [
  ...PERMISSION_CREATE_PRODUCTS,
  ...PERMISSION_EDIT_PRODUCTS,
];

const ProductCustomProperties = React.memo((props) => {
  const { productId, queryLeftProperties = {} } = props;

  const { expandAll, onlyShowValues } = queryLeftProperties;

  const [activeKeyPanels, setActivePanels] = useState([]);
  const [visibleModal, setVisibleModal] = useState(false);

  const hasPermissionEditProperties = useCheckPermissions(
    customPropertiesEditPermissions
  );

  useEffect(() => {
    if (expandAll) {
      setActivePanels('Private Properties');
    } else {
      setActivePanels([]);
    }
  }, [expandAll]);

  const { userInfo } = useUserInfo();
  const memberId = userInfo?.member?.id;

  const { data, refetchProductCustomPropertiesData } =
    useGetProductCustomProperties(productId, memberId);
  const {
    data: memberProductCustomPropertiesData,
    error: getProductCustomPropertiesErrorMessage,
  } = useGetMemberProductCustomProperties(memberId);

  const productCustomProperties = data?.productCustomFieldsData ?? [];
  const memberProductCustomProperties =
    memberProductCustomPropertiesData?.productCustomFields;

  const isDisplayPrivateProperties = onlyShowValues
    ? productCustomProperties.length > 0
    : true;

  const handleCollapseView = (value) => {
    setActivePanels(value);
  };

  const turnOffModal = () => setVisibleModal(false);

  const renderIconEdit = () => {
    const isShowEditBtn =
      hasPermissionEditProperties &&
      activeKeyPanels.includes(keyPanel);

    return (
      <>
        {isShowEditBtn && (
          <Tooltip placement='left' title={`Edit Custom Properties`}>
            <EditOutlined
              className='product-detail-view__edit-icon'
              onClick={(event) => {
                event.stopPropagation();
                setVisibleModal(true);
              }}
            />
          </Tooltip>
        )}
      </>
    );
  };

  return (
    <>
      {isDisplayPrivateProperties && (
        <div className='product-detail-view__private-properties'>
          <CollapseView
            activeKey={activeKeyPanels}
            headerPanel={
              <Typography.Title
                level={5}
                className={classnames('product-detail-view__list-info-title')}
              >
                Custom Properties
              </Typography.Title>
            }
            keyPanel={keyPanel}
            expandIconPosition='left'
            extraPanel={() => renderIconEdit()}
            onChange={handleCollapseView}
          >
            <ViewInfoCustomProperties
              productCustomProperties={productCustomProperties}
              memberProductCustomProperties={memberProductCustomProperties}
            />
          </CollapseView>
        </div>
      )}

      <FormEditCustomProductProperties
        productId={productId}
        visibleModal={visibleModal}
        turnOffModal={turnOffModal}
        memberProductCustomProperties={memberProductCustomProperties}
        productCustomProperties={productCustomProperties}
        refetchProductCustomPropertiesData={refetchProductCustomPropertiesData}
        error={getProductCustomPropertiesErrorMessage}
      />
    </>
  );
});

const ViewInfoCustomProperties = ({
  memberProductCustomProperties,
  productCustomProperties,
}) => {
  const getDisplayName = (property) => {
    const memberProperty = memberProductCustomProperties?.find(
      (propertyItem) => propertyItem.fieldName === property.fieldName
    );
    return (
      memberProperty?.displayName || property.displayName || property.fieldName
    );
  };

  const renderValue = (property) => {
    const propertyData =
      memberProductCustomProperties?.find(
        (propertyItem) => propertyItem.fieldName === property.fieldName
      ) || property;

    let value = property?.value;

    const hasValue = value !== null && value !== undefined && value !== '';

    if (!hasValue || !propertyData) return null;

    const isBoolean =
      propertyData?.fieldType === 'boolean' ||
      propertyData?.fieldType === 'bool';
    const booleanValue =
      isBoolean && value?.toString()?.toLowerCase() === 'true' ? true : false;

    if (isBoolean) {
      return (
        <ColumnContainer style={{ textAlign: 'left', paddingLeft: 8 }}>
          <Checkbox checked={booleanValue} />
        </ColumnContainer>
      );
    }

    if (propertyData?.fieldType === 'number') {
      value = parseFloat(value);
    }

    return (
      <TextInfo ellipsis style={{ whiteSpace: 'pre-wrap' }}>
        {value}
      </TextInfo>
    );
  };

  return (
    <>
      {productCustomProperties?.length > 0
        ? productCustomProperties?.map((property) => {
            const shouldDisplaying =
              property?.value !== null &&
              property?.value !== undefined &&
              property?.value !== '';
            const displayName = getDisplayName(property);

            return shouldDisplaying ? (
              <div
                key={property.fieldName}
                className='product-detail-view__row-view-product'
              >
                <Row>
                  <Col
                    style={{
                      textAlign: 'right',
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                    xs={12}
                    className='product-detail__text-info--label'
                  >
                    <Tooltip title={displayName}>
                      <TextInfo
                        strong
                        ellipsis
                        className={'product-detail__text-info--label'}
                      >
                        {displayName}
                      </TextInfo>
                    </Tooltip>
                    :
                  </Col>
                  <Col style={{ textAlign: 'left' }} xs={12}>
                    {renderValue(property)}
                  </Col>
                </Row>
              </div>
            ) : null;
          })
        : null}
    </>
  );
};

export default ProductCustomProperties;
